import { ref, computed } from 'vue'
import { debounce } from 'lodash'
import apiOpenUseCase from '@/usecase/apiOpenUseCase'

export interface IPositionItem {
  id: string
  name: string
  code: string
  createdAt?: string
  updatedAt?: string
}

export interface IFilterPill {
  id: string
  category: string
  value: string
  label: string
}

export function useCrewPositions(onFilterChange?: () => void) {
  const positions = ref<IPositionItem[]>([])
  const appliedPositionIds = ref<string[]>([])
  const isLoadingPositions = ref(false)
  const pageNumber = ref(1)
  const pageSize = ref(20)
  const totalPages = ref(1)
  const searchPositionQuery = ref('')

  const hasMorePositions = computed(() => pageNumber.value < totalPages.value)

  const positionOptions = computed(() => positions.value.map((item) => ({
    value: item.id,
    label: item.name
  })))

  const activeFilters = computed<IFilterPill[]>(() => appliedPositionIds.value.map((id) => {
    const matched = positions.value.find((p) => p.id === id)
    return {
      id: `position_${id}`,
      category: 'position',
      value: id,
      label: matched ? matched.name : id
    }
  }))

  const fetchPositions = async (reset = false) => {
    if (reset) {
      pageNumber.value = 1
    }
    isLoadingPositions.value = true
    try {
      const params: string[] = [
        `pageNumber=${pageNumber.value}`,
        `pageSize=${pageSize.value}`
      ]
      if (searchPositionQuery.value.trim()) {
        params.push(`name=${encodeURIComponent(searchPositionQuery.value.trim())}`)
      }
      const queryStr = `?${params.join('&')}`
      const response = await apiOpenUseCase.get('/talent/v1/positions', queryStr)
      const data = response?.result?.data ?? response?.result ?? []
      totalPages.value = response?.result?.totalPages ?? 1

      if (reset || pageNumber.value === 1) {
        positions.value = data
      } else {
        const existingIds = new Set(positions.value.map((p) => p.id))
        const newItems = data.filter((p: IPositionItem) => !existingIds.has(p.id))
        positions.value = [...positions.value, ...newItems]
      }
    } catch (err) {
      console.error('Error fetching positions:', err)
    } finally {
      isLoadingPositions.value = false
    }
  }

  const debouncedSearch = debounce(() => {
    fetchPositions(true)
  }, 300)

  const handlePositionSearch = (query: string) => {
    searchPositionQuery.value = query
    debouncedSearch()
  }

  const handleScrollEnd = () => {
    if (isLoadingPositions.value || !hasMorePositions.value) return
    pageNumber.value += 1
    fetchPositions(false)
  }

  const removeFilter = (pill: { value: string }) => {
    appliedPositionIds.value = appliedPositionIds.value.filter((val) => val !== pill.value)
    if (onFilterChange) {
      onFilterChange()
    }
  }

  const clearAllFilters = () => {
    appliedPositionIds.value = []
    if (onFilterChange) {
      onFilterChange()
    }
  }

  return {
    positions,
    positionOptions,
    appliedPositionIds,
    activeFilters,
    isLoadingPositions,
    hasMorePositions,
    searchPositionQuery,
    fetchPositions,
    handlePositionSearch,
    handleScrollEnd,
    removeFilter,
    clearAllFilters
  }
}
